import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Mail, Calendar, Send } from 'lucide-react';
import { submitStrategyCallForm, submitNewsletterForm, submitGatedContentForm } from '@/hubspot/hubspot-forms-api';

// ══════════════════════════════════
// Strategy Call Modal
// ══════════════════════════════════
export function StrategyCallModal({ isOpen, onClose }) {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', company: '', phone: '' });
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const update = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.company) {
      setErrorMsg('Please fill all required fields.');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    const result = await submitStrategyCallForm(form);
    if (result.success) setStatus('success');
    else { setStatus('error'); setErrorMsg(result.error || 'Something went wrong.'); }
  };

  const reset = () => {
    setForm({ firstName: '', lastName: '', email: '', company: '', phone: '' });
    setStatus('idle');
    setErrorMsg('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={reset}>
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }} className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7" onClick={(e) => e.stopPropagation()} data-testid="strategy-call-modal">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-orange-500" />
                <h3 className="text-navy-800 font-bold text-base">Book a Strategy Call</h3>
              </div>
              <button onClick={reset} className="text-navy-400 hover:text-navy-600" data-testid="strategy-close">
                <X size={18} />
              </button>
            </div>

            {status === 'success' ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Check size={24} className="text-green-600" />
                </div>
                <h4 className="text-navy-800 font-bold text-lg mb-2" data-testid="strategy-success">Request Received</h4>
                <p className="text-navy-600 text-sm">We will reach out within 1 business day to schedule your call, {form.firstName}.</p>
                <button onClick={reset} className="mt-4 text-orange-500 text-sm font-medium">Close</button>
              </motion.div>
            ) : (
              <>
                <p className="text-navy-500 text-sm mb-5">30-minute call with a senior technologist. No pitch, just an honest conversation about your challenges.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <input type="text" required placeholder="First name *" value={form.firstName} onChange={(e) => update('firstName', e.target.value)} data-testid="strategy-first" className="bg-navy-50 border border-navy-200 rounded-lg px-4 py-3 text-sm placeholder:text-navy-300 focus:outline-none focus:border-orange-500/50" />
                    <input type="text" placeholder="Last name" value={form.lastName} onChange={(e) => update('lastName', e.target.value)} data-testid="strategy-last" className="bg-navy-50 border border-navy-200 rounded-lg px-4 py-3 text-sm placeholder:text-navy-300 focus:outline-none focus:border-orange-500/50" />
                  </div>
                  <input type="email" required placeholder="Work email *" value={form.email} onChange={(e) => update('email', e.target.value)} data-testid="strategy-email" className="w-full bg-navy-50 border border-navy-200 rounded-lg px-4 py-3 text-sm placeholder:text-navy-300 focus:outline-none focus:border-orange-500/50" />
                  <input type="text" required placeholder="Company *" value={form.company} onChange={(e) => update('company', e.target.value)} data-testid="strategy-company" className="w-full bg-navy-50 border border-navy-200 rounded-lg px-4 py-3 text-sm placeholder:text-navy-300 focus:outline-none focus:border-orange-500/50" />
                  <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={(e) => update('phone', e.target.value)} data-testid="strategy-phone" className="w-full bg-navy-50 border border-navy-200 rounded-lg px-4 py-3 text-sm placeholder:text-navy-300 focus:outline-none focus:border-orange-500/50" />
                  {errorMsg && <p className="text-red-500 text-xs">{errorMsg}</p>}
                  <button type="submit" disabled={status === 'submitting'} data-testid="strategy-submit" className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-full py-3.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors">
                    <Send size={14} /> {status === 'submitting' ? 'Sending...' : 'Request Strategy Call'}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ══════════════════════════════════
// Newsletter Signup (inline)
// ══════════════════════════════════
export function NewsletterSignup({ className = '' }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('submitting');
    const result = await submitNewsletterForm({ email: email.trim() });
    setStatus(result.success ? 'success' : 'idle');
  };

  if (status === 'success') {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`flex items-center gap-2 text-green-500 text-sm font-medium ${className}`} data-testid="newsletter-success">
        <Check size={16} /> You are in. Check your inbox for confirmation.
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`flex gap-3 ${className}`} data-testid="newsletter-form">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" data-testid="newsletter-email" className="flex-1 bg-white/10 border border-white/20 rounded-full px-5 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-orange-500/50" />
      <button type="submit" disabled={status === 'submitting'} data-testid="newsletter-submit" className="bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-full px-7 py-3 text-sm font-medium flex-shrink-0 flex items-center gap-2 transition-colors">
        <Mail size={14} /> {status === 'submitting' ? '...' : 'Subscribe'}
      </button>
    </form>
  );
}

// ══════════════════════════════════
// Gated Content Form (modal)
// ══════════════════════════════════
export function GatedContentModal({ isOpen, onClose, content, onSuccess }) {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', company: '' });
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email) return;
    setStatus('submitting');
    const result = await submitGatedContentForm({
      ...form,
      contentId: content?.id || '',
      contentTitle: content?.title || '',
    });
    if (result.success) {
      setStatus('idle');
      setForm({ firstName: '', lastName: '', email: '', company: '' });
      onSuccess?.(content?.id);
      onClose();
    } else {
      setStatus('idle');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && content && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }} className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7" onClick={(e) => e.stopPropagation()} data-testid="gated-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-navy-800 font-bold text-base">Unlock: {content.title}</h3>
              <button onClick={onClose} className="text-navy-400 hover:text-navy-600" data-testid="gated-modal-close"><X size={18} /></button>
            </div>
            <p className="text-navy-500 text-sm mb-5">Enter your details to access this {content.type?.toLowerCase() || 'download'}. We will send the download link to your email.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <input type="text" required placeholder="First name *" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} data-testid="gate-first-name" className="bg-navy-50 border border-navy-200 rounded-lg px-4 py-3 text-sm placeholder:text-navy-300 focus:outline-none focus:border-orange-500/50" />
                <input type="text" placeholder="Last name" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} data-testid="gate-last-name" className="bg-navy-50 border border-navy-200 rounded-lg px-4 py-3 text-sm placeholder:text-navy-300 focus:outline-none focus:border-orange-500/50" />
              </div>
              <input type="email" required placeholder="Work email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="gate-email" className="w-full bg-navy-50 border border-navy-200 rounded-lg px-4 py-3 text-sm placeholder:text-navy-300 focus:outline-none focus:border-orange-500/50" />
              <input type="text" placeholder="Company (optional)" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="gate-company" className="w-full bg-navy-50 border border-navy-200 rounded-lg px-4 py-3 text-sm placeholder:text-navy-300 focus:outline-none focus:border-orange-500/50" />
              <button type="submit" disabled={status === 'submitting'} data-testid="gate-submit" className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-full py-3.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors">
                {status === 'submitting' ? 'Unlocking...' : 'Get the Download'}
              </button>
            </form>
            <p className="text-navy-400 text-xs mt-3 text-center">No spam. We respect your inbox.</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
